"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import DomFlag from "../../../public/dpmflag.png";

interface DomainData {
  id: number;
  extension: string;
  name: string;     // country name
  maincat: string;  // Generic, European, Asia-Pacific, etc
  pricing: any;
}

/* ---------------- HELPERS ---------------- */

const normalizeExt = (v: string) =>
  v.replace(/^\./, "").trim().toLowerCase();

const slugifyCountry = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "_");

const getBestPrice = (pricing: any) =>
  pricing?.domainregister?.msetupfee ??
  pricing?.domainregister?.qsetupfee ??
  pricing?.domainregister?.ssetupfee ??
  null;

/* ---------------- COMPONENT ---------------- */

const DomainTable: React.FC = () => {
  const [domains, setDomains] = useState<DomainData[]>([]);

  const categories = useMemo(
    () => [
      "Generic",
      "European",
      "Asia-Pacific",
      "African",
      "American",
      "Geographic",
      "Community",
    ],
    []
  );

  useEffect(() => {
    const fetchDomains = async () => {
      const res = await fetch("/api/domains", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currency: 1, getprice: true }),
      });

      const data = await res.json();
      setDomains(data.domains || []);
    };

    fetchDomains();
  }, []);

  /* ---- group domains by category ---- */
  const groupedDomains = useMemo(() => {
    const map: Record<string, DomainData[]> = {};

    categories.forEach((c) => (map[c] = []));

    domains.forEach((d) => {
      if (!map[d.maincat]) return;

      // prevent duplicate extensions per category
      if (map[d.maincat].some((x) => x.extension === d.extension)) return;

      map[d.maincat].push(d);
    });

    return map;
  }, [domains, categories]);

  return (
    <>
      {categories.map((cat) => (
        <div key={cat} className="custMargin">
          <div className="container">
            <h2 className="fw-bold fs-4 mb-4">
              {cat} Domains
            </h2>

            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>TLD</th>
                    <th>Country</th>
                    <th>Term</th>
                    <th>Price</th>
                  </tr>
                </thead>

                <tbody>
                  {groupedDomains[cat]?.map((d) => {
                    const countrySlug = slugifyCountry(d.name);
                    const ext = normalizeExt(d.extension);
                    const price = getBestPrice(d.pricing);

                    const url = `/country-domains/${countrySlug}-.${ext}`;

                    return (
                      <tr key={`${cat}-${d.extension}`}>
                        <td>
                          <Link href={url} className="textHad fw-bold text-decoration-none">.{ext}</Link>
                        </td>

                        <td>
                          <Image
                            src={DomFlag}
                            width={26}
                            height={17}
                            alt="flag"
                            className="me-2"
                          />
                          {d.name}
                        </td>

                        <td>1 Year</td>

                        <td>
                          {process.env.NEXT_PUBLIC_CURRENCY_SYMBOL}
                          {price ?? "N/A"}
                        </td>
                      </tr>
                    );
                  })}

                  {!groupedDomains[cat]?.length && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        No domains available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default DomainTable;
